import type { StoreApi } from 'zustand'
import {
  assertFormativeTaskSessionCanResume,
  createFormativeTaskSession,
  createFormativeTaskSessionId,
  createFormativeQuestionInstanceId,
  submitFormativeTaskAnswer,
} from '@/entities/formative-task-session'
import type { AppState } from '@/store/rootTypes'
import { activeWorkspacePatch } from '@/store/workspace'
import { projectLearningAttemptPatch } from '@/store/slices/learningSlice'
import { createFormativeTaskRewardClaimId } from '@/entities/reward-claim'
import { createLearningTaskCompletionKey } from '@/entities/progress'

type SetState = StoreApi<AppState>['setState']

export function createFormativeSessionSlice(set: SetState): Pick<
  AppState,
  'startFormativeTaskSession' | 'submitFormativeTaskAnswer' | 'completeFormativeTaskSession'
> {
  return {
    startFormativeTaskSession: (input) => {
      let result: ReturnType<AppState['startFormativeTaskSession']> | null = null
      set((state) => {
        if (state.activeStudentId !== input.studentId || !state.workspaces[input.studentId]) {
          throw new Error('Formative task session must belong to the active student workspace.')
        }
        const sessionId = createFormativeTaskSessionId(input)
        const existing = state.formativeTaskSessions.find((session) => session.id === sessionId)
        if (existing) {
          assertFormativeTaskSessionCanResume(existing, input)
          result = existing
          return state
        }
        const session = createFormativeTaskSession(input)
        const patch = { formativeTaskSessions: [...state.formativeTaskSessions, session] }
        result = session
        return { ...patch, ...activeWorkspacePatch(state, patch) }
      })
      if (!result) throw new Error('Formative task session did not start.')
      return result
    },
    submitFormativeTaskAnswer: (input) => {
      let result: ReturnType<AppState['submitFormativeTaskAnswer']> | null = null
      set((state) => {
        const sessionIndex = state.formativeTaskSessions.findIndex((session) => session.id === input.sessionId)
        if (sessionIndex < 0) throw new Error(`Formative task session ${input.sessionId} was not found.`)
        const session = state.formativeTaskSessions[sessionIndex]
        const expectedExerciseId = createFormativeQuestionInstanceId({
          studentId: session.studentId,
          stageId: session.stageId,
          taskId: session.taskId,
          contentVersion: session.contentVersion,
          questionId: input.submission.questionId,
        })
        const attempt = input.attempt
        if (input.submission.correct === null) {
          if (attempt) {
            throw new Error('An unobserved formative question cannot project a learning attempt.')
          }
        } else {
          if (!attempt
            || attempt.studentId !== session.studentId
            || attempt.stageId !== session.stageId
            || attempt.unitId !== session.unitId
            || attempt.taskId !== session.taskId
            || attempt.exerciseId !== expectedExerciseId
            || attempt.correct !== input.submission.correct
            || attempt.evidenceKind !== 'formative') {
            throw new Error('Formative task attempt identity does not match its session submission.')
          }
        }

        const transition = submitFormativeTaskAnswer(session, input.submission)
        result = transition
        if (transition.status === 'duplicate') return state

        const formativeTaskSessions = state.formativeTaskSessions.map((candidate, index) => (
          index === sessionIndex ? transition.session : candidate
        ))
        if (!attempt) {
          const workspacePatch = { formativeTaskSessions }
          return { ...workspacePatch, ...activeWorkspacePatch(state, workspacePatch) }
        }
        const projection = projectLearningAttemptPatch(state, attempt)
        const workspacePatch = { ...projection.workspacePatch, formativeTaskSessions }
        return {
          ...workspacePatch,
          pendingAchievements: projection.pendingAchievements,
          ...activeWorkspacePatch(state, workspacePatch),
        }
      })
      if (!result) throw new Error('Formative task answer did not produce a receipt.')
      return result
    },
    completeFormativeTaskSession: (input) => {
      let result: ReturnType<AppState['completeFormativeTaskSession']> | null = null
      set((state) => {
        const session = state.formativeTaskSessions.find((candidate) => candidate.id === input.sessionId)
        if (!session) throw new Error(`Formative task session ${input.sessionId} was not found.`)
        if (session.studentId !== state.activeStudentId || !state.workspaces[session.studentId]) {
          throw new Error('Formative task completion must belong to the active student workspace.')
        }
        if (session.phase !== 'completed' || !session.completedAt) {
          throw new Error(`Formative task session ${session.id} is not completed.`)
        }
        const rewardClaimId = createFormativeTaskRewardClaimId(session.id)
        const completionKey = createLearningTaskCompletionKey(session.stageId, session.taskId)
        const duplicate = Boolean(state.completedTasks[completionKey])
          || state.rewardClaims.some((claim) => claim.id === rewardClaimId)
        const completedAt = state.taskCompletedAt[completionKey]
          ?? input.completedAt
          ?? session.completedAt
        if (duplicate) {
          result = { status: 'duplicate', completedAt, awardedStars: 0 }
          return state
        }

        const taskIds = [...new Set([
          completionKey,
          ...input.taskIds.filter((taskId) => taskId !== session.taskId),
        ])]
        const completedTasks = { ...state.completedTasks }
        const taskCompletedAt = { ...state.taskCompletedAt }
        taskIds.forEach((taskId) => {
          completedTasks[taskId] = true
          taskCompletedAt[taskId] ??= completedAt
        })
        const awardedStars = Math.max(0, input.reward?.stars ?? 0)
        const stars = state.stars + awardedStars
        const student = awardedStars > 0
          ? { ...state.student, totalStars: state.student.totalStars + awardedStars }
          : state.student
        const rewardClaims = input.reward
          ? [...state.rewardClaims, {
              id: rewardClaimId,
              kind: 'formative-task' as const,
              status: 'pending' as const,
              studentId: session.studentId,
              stageId: session.stageId,
              unitId: session.unitId,
              formativeTaskSessionId: session.id,
              taskId: session.taskId,
              stars: awardedStars,
              plantExperienceAwards: Object.fromEntries(
                Object.entries(input.reward.plantExperienceAwards)
                  .map(([zone, amount]) => [zone, Math.max(0, amount ?? 0)]),
              ),
              starsGrantedAt: completedAt,
              createdAt: completedAt,
            }]
          : state.rewardClaims
        const patch = {
          completedTasks,
          taskCompletedAt,
          stars,
          student,
          rewardClaims,
          weeklyPlanActive: true,
          weeklyPlanStartDate: state.weeklyPlanStartDate ?? completedAt,
        }
        result = {
          status: 'completed',
          completedAt,
          awardedStars,
          ...(input.reward ? { rewardClaimId } : {}),
        }
        return { ...patch, ...activeWorkspacePatch(state, patch) }
      })
      if (!result) throw new Error('Formative task completion did not produce a receipt.')
      return result
    },
  }
}
